import React, { useState } from "react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/auth/AuthContext";

const roleStyle = {
  Owner: "bg-[#1E3D2B] text-white",
  Administrator: "bg-[#ECF3EC] text-[#3E7256]",
  Analyst: "bg-[#ECFDF5] text-[#059669]",
  Member: "bg-slate-100 text-slate-600",
  Viewer: "bg-[#FFFBEB] text-[#D97706]",
};

export default function Profile() {
  const { user, memberships, setUser } = useAuth();
  const [name, setName] = useState(user?.name || "");
  const [saving, setSaving] = useState(false);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const { data } = await api.patch("/auth/profile", { name });
      setUser(data.user);
      toast.success("Profil mis à jour");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail, err.message));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-10" data-testid="profile-page">
      <div>
        <h1 className="font-heading text-2xl font-bold tracking-tight text-[#1E3D2B] sm:text-3xl">
          Profil utilisateur
        </h1>
        <p className="mt-2 text-sm text-slate-500">Gérez vos informations personnelles et vos organisations.</p>
      </div>

      <section className="rounded-2xl border border-slate-200 bg-white p-8">
        <h2 className="font-heading text-lg font-semibold text-[#1E3D2B]">Informations</h2>
        <form onSubmit={save} className="mt-6 max-w-md space-y-5" data-testid="profile-form">
          <div className="space-y-1.5">
            <label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Nom complet</label>
            <input
              value={name}
              onChange={(e) => setName(e.target.value)}
              data-testid="profile-name"
              className="h-11 w-full rounded-lg border border-slate-200 bg-white px-3.5 text-sm outline-none focus:border-[#5C9873] focus:ring-2 focus:ring-[#5C9873]/15"
              required
            />
          </div>
          <div className="space-y-1.5">
            <label className="text-xs font-semibold uppercase tracking-wider text-slate-500">E-mail</label>
            <input
              value={user?.email || ""}
              disabled
              data-testid="profile-email"
              className="h-11 w-full rounded-lg border border-slate-200 bg-slate-50 px-3.5 text-sm text-slate-400"
            />
          </div>
          <button
            type="submit"
            disabled={saving}
            data-testid="profile-save"
            className="flex h-11 items-center justify-center rounded-lg bg-[#1E3D2B] px-6 text-sm font-semibold text-white hover:bg-[#2C5540] disabled:opacity-60"
          >
            {saving ? "Enregistrement…" : "Enregistrer"}
          </button>
        </form>
      </section>

      <section className="rounded-2xl border border-slate-200 bg-white p-8" data-testid="profile-orgs">
        <h2 className="font-heading text-lg font-semibold text-[#1E3D2B]">Mes organisations</h2>
        <div className="mt-6 divide-y divide-slate-100">
          {memberships.map((m) => (
            <div key={m.org.id} className="flex items-center justify-between py-4 first:pt-0 last:pb-0">
              <div>
                <p className="text-sm font-semibold text-[#1E3D2B]">{m.org.name}</p>
                <p className="text-xs text-slate-400">{m.org.type}</p>
              </div>
              <span className={`rounded-full px-2.5 py-1 text-xs font-semibold ${roleStyle[m.role] || "bg-slate-100 text-slate-600"}`}>
                {m.role}
              </span>
            </div>
          ))}
          {memberships.length === 0 && (
            <p className="py-4 text-sm text-slate-400">Aucune organisation pour le moment.</p>
          )}
        </div>
      </section>
    </div>
  );
}
